import type { WorkspaceServerService } from '@affine/core/modules/cloud';
import type { WorkspaceDBService } from '@affine/core/modules/db';
import type { WorkspaceService } from '@affine/core/modules/workspace';
import {
  DocRole,
  type GrantDocUserRolesInput,
  grantDocUserRolesMutation,
} from '@affine/graphql';
import { Service } from '@toeverything/infra';

import type { FolderStore } from '../../organize/stores/folder';

export type FolderSharePolicy = {
  folderId: string;
  userIds: string[];
  role: DocRole;
};

export type FolderShareResult = {
  docIds: string[];
  successDocIds: string[];
  failedDocIds: string[];
};

const roleWeight = new Map<DocRole, number>([
  [DocRole.Reader, 1],
  [DocRole.Commenter, 2],
  [DocRole.Editor, 3],
  [DocRole.Manager, 4],
]);

const normalizeRole = (role: string): DocRole => {
  if (
    role === DocRole.Reader ||
    role === DocRole.Commenter ||
    role === DocRole.Editor ||
    role === DocRole.Manager
  ) {
    return role;
  }
  return DocRole.Reader;
};

export class FolderShareService extends Service {
  constructor(
    private readonly folderStore: FolderStore,
    private readonly dbService: WorkspaceDBService,
    private readonly workspaceServerService: WorkspaceServerService,
    private readonly workspaceService: WorkspaceService
  ) {
    super();
  }

  collectDocIds(folderId: string): string[] {
    return this.folderStore.collectDocIds(folderId);
  }

  getPolicy(folderId: string): FolderSharePolicy | null {
    const policy = this.dbService.db.folderSharePolicies.get(folderId);
    if (!policy) {
      return null;
    }
    return {
      folderId: policy.folderId,
      userIds: policy.userIds,
      role: normalizeRole(policy.role),
    };
  }

  async shareFolder(folderId: string, userIds: string[], role: DocRole) {
    const docIds = this.folderStore.collectDocIds(folderId);
    const result = await this.grantDocs(docIds, userIds, role);
    this.savePolicy(folderId, userIds, role);
    return result;
  }

  clearPolicy(folderId: string) {
    if (this.dbService.db.folderSharePolicies.get(folderId)) {
      this.dbService.db.folderSharePolicies.delete(folderId);
    }
  }

  async applyPoliciesToFolder(folderId: string, policyFolderId = folderId) {
    const docIds = this.folderStore.collectDocIds(folderId);
    await Promise.all(
      docIds.map(docId => this.applyPoliciesToDoc(docId, policyFolderId))
    );
  }

  async applyPoliciesToDoc(docId: string, folderId: string) {
    const policies = this.getInheritedPolicies(folderId);
    if (!policies.length) {
      return;
    }

    const userRole = new Map<string, DocRole>();
    for (const policy of policies) {
      for (const userId of policy.userIds) {
        const previous = userRole.get(userId);
        if (
          !previous ||
          (roleWeight.get(policy.role) ?? 0) > (roleWeight.get(previous) ?? 0)
        ) {
          userRole.set(userId, policy.role);
        }
      }
    }

    const roleGroups = new Map<DocRole, string[]>();
    for (const [userId, role] of userRole) {
      roleGroups.set(role, [...(roleGroups.get(role) ?? []), userId]);
    }

    await Promise.all(
      [...roleGroups].map(([role, userIds]) =>
        this.grantDoc(docId, userIds, role)
      )
    );
  }

  private savePolicy(folderId: string, userIds: string[], role: DocRole) {
    const existing = this.dbService.db.folderSharePolicies.get(folderId);
    if (existing) {
      this.dbService.db.folderSharePolicies.update(folderId, {
        userIds,
        role,
      });
    } else {
      this.dbService.db.folderSharePolicies.create({
        folderId,
        userIds,
        role,
      });
    }
  }

  private getInheritedPolicies(folderId: string) {
    const policies: FolderSharePolicy[] = [];
    const visited = new Set<string>();
    let currentId: string | null | undefined = folderId;

    while (currentId && !visited.has(currentId)) {
      visited.add(currentId);
      const policy = this.getPolicy(currentId);
      if (policy) {
        policies.push(policy);
      }
      currentId = this.dbService.db.folders.get(currentId)?.parentId;
    }

    return policies;
  }

  private async grantDocs(
    docIds: string[],
    userIds: string[],
    role: DocRole
  ): Promise<FolderShareResult> {
    const successDocIds: string[] = [];
    const failedDocIds: string[] = [];

    for (const docId of docIds) {
      try {
        await this.grantDoc(docId, userIds, role);
        successDocIds.push(docId);
      } catch {
        failedDocIds.push(docId);
      }
    }

    return { docIds, successDocIds, failedDocIds };
  }

  private async grantDoc(docId: string, userIds: string[], role: DocRole) {
    if (!userIds.length) {
      return;
    }
    if (!this.workspaceServerService.server) {
      throw new Error('No Server');
    }

    const input: GrantDocUserRolesInput = {
      workspaceId: this.workspaceService.workspace.id,
      docId,
      userIds,
      role,
    };

    await this.workspaceServerService.server.gql({
      query: grantDocUserRolesMutation,
      variables: { input },
    });
  }
}
